/**
 * /qr-tracking — flat wire for the QR tracking operator page.
 *
 * The feature dir `features/qr-tracking/` exists but had no route file yet.
 * Dash contract rule D1 requires `<slug>.tsx` or `*.<slug>.tsx`; this thin
 * route file satisfies D1.
 *
 * `?affiliateId=<id>` deep-links into a specific affiliate's QR set.
 */

import { Link, createRoute } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { QrTrackingPage } from "@/features/qr-tracking/QrTrackingPage";

interface QrTrackingSearch {
	affiliateId?: string;
}

export const qrTrackingRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/qr-tracking",
	validateSearch: (raw: Record<string, unknown>): QrTrackingSearch => {
		const affiliateId = raw.affiliateId;
		if (typeof affiliateId === "string" && affiliateId.trim().length > 0) {
			return { affiliateId: affiliateId.trim() };
		}
		return {};
	},
	component: QrTrackingRouteComponent,
});

export default qrTrackingRoute;

export function QrTrackingRouteComponent() {
	const { affiliateId } = qrTrackingRoute.useSearch();

	return (
		<main className="mx-auto w-full max-w-7xl px-6 py-6">
			<nav aria-label="Breadcrumb" className="mb-4 text-sm text-zinc-500">
				<ol className="flex flex-wrap items-center gap-1">
					<li>
						<Link
							to="/"
							className="rounded-md px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
						>
							Dashboard
						</Link>
					</li>
					<li aria-hidden="true" className="text-zinc-300">
						/
					</li>
					<li>
						<span className="rounded-md px-1 py-0.5 text-zinc-600">
							QR tracking
						</span>
					</li>
				</ol>
			</nav>
			<QrTrackingPage initialAffiliateId={affiliateId} />
		</main>
	);
}
